import { useQuery } from "@tanstack/react-query";
import { ContentPageTheme, ContentPageThemeServices, GetPageThemesParams } from "@/services/bedrock";
import * as React from "react";

export function usePageThemes(params: GetPageThemesParams = { page: 1, perPage: 50 }, initialThemeId?: number) {
  const { page = 1, perPage = 50, search } = params;

  // Query for paginated themes list
  const { data: pageThemesRes, isLoading: isThemesLoading } = useQuery({
    queryKey: ["contentPageThemesList", page, perPage, search],
    queryFn: () => ContentPageThemeServices.getPageThemes({ page, perPage, search }),
    placeholderData: prev => prev,
  });

  // Query for the specific initial theme if it's set
  const { data: initialTheme, isLoading: isInitialThemeLoading } = useQuery({
    queryKey: ["contentPageTheme", initialThemeId],
    queryFn: () => ContentPageThemeServices.getPageTheme(initialThemeId!),
    enabled: !!initialThemeId,
  });

  // Combine themes, ensuring initial theme is present
  const themes = React.useMemo(() => {
    const fetchedThemes = pageThemesRes?.content || [];
    if (initialTheme && !fetchedThemes.some(t => t.id === initialTheme.id)) {
      return [...fetchedThemes, initialTheme];
    }
    return fetchedThemes;
  }, [pageThemesRes, initialTheme]);

  const isLoading = isThemesLoading || (!!initialThemeId && isInitialThemeLoading);

  return {
    themes,
    isLoading,
    rawResponse: pageThemesRes,
  };
}
